// Document Management Types
// These types match the backend enums and DTOs

export enum DocumentOwnerType {
  Company = 1,
  Partner = 2,
  Individual = 3,
}

export enum DocumentSphere {
  Federal = 1,
  Estadual = 2,
  Municipal = 3,
  Judicial = 4,
  Profissional = 5,
  Permanente = 6,
}

export enum CompanyDocumentType {
  ECNPJ = 1,
  ContratoSocial = 2,
  Alvara = 3,
  InscricaoEstadual = 4,
  InscricaoMunicipal = 5,
  CartaoCNPJ = 6,
  DARFDAS = 7,
  CertidaoNegativa = 8,
  ContratoLocacao = 9,
  ComprovanteEndereco = 10,
  // New document types with sphere and validity
  CertidaoConjuntaReceitaFederalPGFN = 11,
  CertificadoRegularidadeFGTS = 12,
  CertidaoNegativaDebitosTrabalhistasCNDT = 13,
  CertidaoNegativaDebitosEstaduais = 14,
  CertidaoRegularidadeICMS = 15,
  CertidaoNegativaDebitosMunicipais = 16,
  CertidaoFalenciaConcordataRecuperacaoJudicial = 17,
  CertificadoEntidadeClasse = 18,
  CertidaoRegularidadeINSS = 19,
  AlvaraFuncionamento = 20,
  AlvaraVigilanciaSanitaria = 21,
  LicencaCorpoBombeiros = 22,
  Other = 99,
}

export enum PartnerDocumentType {
  ECPF = 1,
  RG = 2,
  CPF = 3,
  CNH = 4,
  ComprovanteResidencia = 5,
  EstadoCivil = 6,
  CertidaoCasamento = 7,
  CertidaoDivorcio = 8,
  TituloEleitor = 9,
  Procuracao = 10,
  Other = 99,
}

export enum IndividualDocumentType {
  ECPF = 1,
  RG = 2,
  CNH = 3,
  ProofOfAddress = 4,
  BirthCertificate = 5,
  MarriageCertificate = 6,
  DivorceCertificate = 7,
  CPFCard = 8,
  VoterRegistration = 9,
  WorkCard = 10,
  Proposta = 11,
  ContratoServico = 12,
  AtaReuniao = 13,
  Other = 99,
}

export enum DocumentStatus {
  Pending = 0,
  Valid = 1,
  Expired = 2,
  ExpiringSoon = 3,
}

export enum DocumentAction {
  Upload = 0,
  Replace = 1,
  Delete = 2,
  Restore = 3,
  StatusChange = 4,
}

// DTOs

export interface CompanyDocumentDto {
  id: string;
  companyId: string;
  documentType: CompanyDocumentType;
  sphere: DocumentSphere;
  fileName: string;
  originalFileName: string;
  contentType: string;
  fileSize: number;
  r2Key: string;
  r2Guid: string;
  version: number;
  startDate?: string;
  validityDays?: number;
  expirationDate?: string;
  status: DocumentStatus;
  notes?: string;
  uploadedByUserId: string;
  updatedByUserId?: string;
  deletedByUserId?: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
  isDeleted: boolean;
  signedUrl?: string;
  canRestore?: boolean;
  restoreIssue?: string;
}

export interface PartnerDocumentDto {
  id: string;
  companyId: string;
  partnerId: string;
  documentType: PartnerDocumentType;
  fileName: string;
  originalFileName: string;
  contentType: string;
  fileSize: number;
  r2Key: string;
  r2Guid: string;
  version: number;
  expirationDate?: string;
  status: DocumentStatus;
  notes?: string;
  uploadedByUserId: string;
  updatedByUserId?: string;
  deletedByUserId?: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
  isDeleted: boolean;
  signedUrl?: string;
  canRestore?: boolean;
  restoreIssue?: string;
}

export interface IndividualDocumentDto {
  id: string;
  individualId: string;
  documentType: IndividualDocumentType;
  fileName: string;
  originalFileName: string;
  contentType: string;
  fileSize: number;
  r2Key: string;
  r2Guid: string;
  version: number;
  expirationDate?: string;
  status: DocumentStatus;
  notes?: string;
  uploadedByUserId: string;
  updatedByUserId?: string;
  deletedByUserId?: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
  isDeleted: boolean;
  signedUrl?: string;
  canRestore?: boolean;
  restoreIssue?: string;
}

export interface CompanyDocumentStatistics {
  totalDocuments: number;
  pendingDocuments: number;
  validDocuments: number;
  expiredDocuments: number;
  expiringSoonDocuments: number;
  deletedDocuments: number;
}

export interface PartnerDocumentStatistics {
  totalDocuments: number;
  pendingDocuments: number;
  validDocuments: number;
  expiredDocuments: number;
  expiringSoonDocuments: number;
  deletedDocuments: number;
}

export interface PartnerDocumentCompleteness {
  hasRG: boolean;
  hasCNH: boolean;
  hasCPF: boolean;
  hasProofOfAddress: boolean;
  hasECPF: boolean; // Optional
  isComplete: boolean;
}

export interface GetDocumentsByCompanyResult {
  companyId: string;
  documents: CompanyDocumentDto[];
  statistics: CompanyDocumentStatistics;
}

export interface GetDocumentsByPartnerResult {
  companyId: string;
  partnerId: string;
  documents: PartnerDocumentDto[];
  statistics: PartnerDocumentStatistics;
  completeness: PartnerDocumentCompleteness;
}

export interface UploadDocumentProgress {
  file: File;
  customFileName?: string; // Allow user to override the filename (useful for Windows 8.3 short names)
  documentType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  documentId?: string;
}

// Helper functions

export function getDocumentTypeLabel(
  type: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType,
  ownerType: DocumentOwnerType
): string {
  if (ownerType === DocumentOwnerType.Company) {
    const companyLabels: Record<CompanyDocumentType, string> = {
      [CompanyDocumentType.ECNPJ]: 'e-CNPJ',
      [CompanyDocumentType.ContratoSocial]: 'Contrato Social',
      [CompanyDocumentType.Alvara]: 'Alvará',
      [CompanyDocumentType.InscricaoEstadual]: 'Inscrição Estadual',
      [CompanyDocumentType.InscricaoMunicipal]: 'Inscrição Municipal',
      [CompanyDocumentType.CartaoCNPJ]: 'Cartão CNPJ',
      [CompanyDocumentType.DARFDAS]: 'DARF/DAS',
      [CompanyDocumentType.CertidaoNegativa]: 'Certidão Negativa',
      [CompanyDocumentType.ContratoLocacao]: 'Contrato de Locação',
      [CompanyDocumentType.ComprovanteEndereco]: 'Comprovante de Endereço',
      [CompanyDocumentType.CertidaoConjuntaReceitaFederalPGFN]: 'Certidão Conjunta Receita Federal/PGFN',
      [CompanyDocumentType.CertificadoRegularidadeFGTS]: 'Certificado Regularidade FGTS',
      [CompanyDocumentType.CertidaoNegativaDebitosTrabalhistasCNDT]: 'Certidão Negativa Débitos Trabalhistas (CNDT)',
      [CompanyDocumentType.CertidaoNegativaDebitosEstaduais]: 'Certidão Negativa Débitos Estaduais',
      [CompanyDocumentType.CertidaoRegularidadeICMS]: 'Certidão Regularidade ICMS',
      [CompanyDocumentType.CertidaoNegativaDebitosMunicipais]: 'Certidão Negativa Débitos Municipais',
      [CompanyDocumentType.CertidaoFalenciaConcordataRecuperacaoJudicial]: 'Certidão Falência/Concordata/Recuperação Judicial',
      [CompanyDocumentType.CertificadoEntidadeClasse]: 'Certificado Entidade de Classe',
      [CompanyDocumentType.CertidaoRegularidadeINSS]: 'Certidão Regularidade INSS',
      [CompanyDocumentType.AlvaraFuncionamento]: 'Alvará de Funcionamento',
      [CompanyDocumentType.AlvaraVigilanciaSanitaria]: 'Alvará Vigilância Sanitária',
      [CompanyDocumentType.LicencaCorpoBombeiros]: 'Licença Corpo de Bombeiros (AVCB)',
      [CompanyDocumentType.Other]: 'Outros Documentos',
    };
    return companyLabels[type as CompanyDocumentType];
  } else if (ownerType === DocumentOwnerType.Partner) {
    const partnerLabels: Record<PartnerDocumentType, string> = {
      [PartnerDocumentType.ECPF]: 'e-CPF',
      [PartnerDocumentType.RG]: 'RG',
      [PartnerDocumentType.CPF]: 'CPF',
      [PartnerDocumentType.CNH]: 'CNH',
      [PartnerDocumentType.ComprovanteResidencia]: 'Comprovante de Residência',
      [PartnerDocumentType.EstadoCivil]: 'Estado Civil',
      [PartnerDocumentType.CertidaoCasamento]: 'Certidão de Casamento',
      [PartnerDocumentType.CertidaoDivorcio]: 'Certidão de Divórcio',
      [PartnerDocumentType.TituloEleitor]: 'Título de Eleitor',
      [PartnerDocumentType.Procuracao]: 'Procuração',
      [PartnerDocumentType.Other]: 'Outros Documentos',
    };
    return partnerLabels[type as PartnerDocumentType];
  } else {
    const individualLabels: Record<IndividualDocumentType, string> = {
      [IndividualDocumentType.ECPF]: 'e-CPF',
      [IndividualDocumentType.RG]: 'RG',
      [IndividualDocumentType.CNH]: 'CNH',
      [IndividualDocumentType.ProofOfAddress]: 'Comprovante de Endereço',
      [IndividualDocumentType.BirthCertificate]: 'Certidão de Nascimento',
      [IndividualDocumentType.MarriageCertificate]: 'Certidão de Casamento',
      [IndividualDocumentType.DivorceCertificate]: 'Certidão de Divórcio',
      [IndividualDocumentType.CPFCard]: 'Cartão CPF',
      [IndividualDocumentType.VoterRegistration]: 'Título de Eleitor',
      [IndividualDocumentType.WorkCard]: 'Carteira de Trabalho',
      [IndividualDocumentType.Proposta]: 'Proposta',
      [IndividualDocumentType.ContratoServico]: 'Contrato de Prestação de Serviços',
      [IndividualDocumentType.AtaReuniao]: 'Ata de Reunião',
      [IndividualDocumentType.Other]: 'Outros Documentos',
    };
    return individualLabels[type as IndividualDocumentType];
  }
}

export function getDocumentStatusLabel(status: DocumentStatus): string {
  const labels: Record<DocumentStatus, string> = {
    [DocumentStatus.Pending]: 'Pendente',
    [DocumentStatus.Valid]: 'Válido',
    [DocumentStatus.Expired]: 'Expirado',
    [DocumentStatus.ExpiringSoon]: 'Expirando em Breve',
  };
  return labels[status];
}

export function getDocumentStatusColor(
  status: DocumentStatus
): 'danger' | 'success' | 'secondary' | 'warning' {
  const colors: Record<DocumentStatus, 'danger' | 'success' | 'secondary' | 'warning'> = {
    [DocumentStatus.Pending]: 'danger',
    [DocumentStatus.Valid]: 'success',
    [DocumentStatus.Expired]: 'secondary',
    [DocumentStatus.ExpiringSoon]: 'warning',
  };
  return colors[status];
}

export function getFileIcon(contentType: string, fileName: string): string {
  // PDF
  if (contentType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return 'ri-file-pdf-line';
  }

  // Images
  if (contentType.startsWith('image/')) {
    return 'ri-image-line';
  }

  // Word Documents
  if (
    contentType === 'application/msword' ||
    contentType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileName.endsWith('.doc') ||
    fileName.endsWith('.docx')
  ) {
    return 'ri-file-word-line';
  }

  // Excel Documents
  if (
    contentType === 'application/vnd.ms-excel' ||
    contentType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    fileName.endsWith('.xls') ||
    fileName.endsWith('.xlsx')
  ) {
    return 'ri-file-excel-line';
  }

  // ZIP/RAR Archives
  if (
    contentType === 'application/zip' ||
    contentType === 'application/x-zip-compressed' ||
    contentType === 'application/x-rar-compressed' ||
    fileName.endsWith('.zip') ||
    fileName.endsWith('.rar') ||
    fileName.endsWith('.7z')
  ) {
    return 'ri-file-zip-line';
  }

  // Certificate Files
  if (
    contentType === 'application/x-pkcs12' ||
    fileName.endsWith('.pfx') ||
    fileName.endsWith('.p12') ||
    fileName.endsWith('.cer') ||
    fileName.endsWith('.crt')
  ) {
    return 'ri-file-shield-line';
  }

  // Text Files
  if (contentType.startsWith('text/')) {
    return 'ri-file-text-line';
  }

  // Default
  return 'ri-file-line';
}

export function formatFileSize(bytes: number): string {
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;

  if (bytes >= GB) {
    return `${(bytes / GB).toFixed(2)} GB`;
  }
  if (bytes >= MB) {
    return `${(bytes / MB).toFixed(2)} MB`;
  }
  if (bytes >= KB) {
    return `${(bytes / KB).toFixed(2)} KB`;
  }

  return `${bytes} bytes`;
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getDocumentSphereLabel(sphere: DocumentSphere): string {
  const labels: Record<DocumentSphere, string> = {
    [DocumentSphere.Federal]: 'Federal',
    [DocumentSphere.Estadual]: 'Estadual',
    [DocumentSphere.Municipal]: 'Municipal',
    [DocumentSphere.Judicial]: 'Judicial',
    [DocumentSphere.Profissional]: 'Profissional',
    [DocumentSphere.Permanente]: 'Permanente',
  };
  return labels[sphere];
}

export function getDocumentSphereColor(
  sphere: DocumentSphere
): 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
  const colors: Record<
    DocumentSphere,
    'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary'
  > = {
    [DocumentSphere.Federal]: 'primary', // Blue
    [DocumentSphere.Estadual]: 'success', // Green
    [DocumentSphere.Municipal]: 'warning', // Orange
    [DocumentSphere.Judicial]: 'danger', // Red
    [DocumentSphere.Profissional]: 'info', // Light Blue/Purple
    [DocumentSphere.Permanente]: 'secondary', // Gray
  };
  return colors[sphere];
}

export function formatValidityDays(days: number | null | undefined): string {
  if (!days) return 'Permanente';

  if (days >= 365) {
    const years = Math.floor(days / 365);
    return years === 1 ? '1 ano' : `${years} anos`;
  }

  if (days >= 30) {
    const months = Math.floor(days / 30);
    return months === 1 ? '1 mês' : `${months} meses`;
  }

  return `${days} dias`;
}
